/**
 * A question's status as dot and word, opening all six statuses, as in the
 * web app. Anyone who can edit the bid can set any status.
 */
import { CheckIcon, ChevronDownIcon } from "@/design/icons";
import type { FC } from "react";

import { cn } from "@/agents-v2/utils/cn";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/design/ui/dropdown-menu";

import { SECTION_STATUSES, type SectionStatus } from "../tender/questionsApi";

const TONE: Record<SectionStatus, string> = {
  "Not Started": "text-status-not-started",
  "In Progress": "text-status-in-progress",
  "Awaiting review": "text-status-awaiting-review",
  "In review": "text-status-in-review",
  Finalising: "text-status-finalising",
  Complete: "text-status-complete",
};

const LABEL: Record<SectionStatus, string> = {
  "Not Started": "Not started",
  "In Progress": "In progress",
  "Awaiting review": "Awaiting review",
  "In review": "In review",
  Finalising: "Finalising",
  Complete: "Complete",
};

export const statusLabel = (s: SectionStatus): string => LABEL[s] ?? s;

export const StatusDot: FC<{ status: SectionStatus; className?: string }> = ({ status, className }) => (
  <span className={cn("inline-flex items-center gap-1.5 text-xs font-semibold", TONE[status], className)}>
    <span className="size-1.5 rounded-full bg-current" />
    {LABEL[status]}
  </span>
);

/** The status as a dot only, named on hover; for rows too narrow for the word. */
export const StatusPip: FC<{ status: SectionStatus }> = ({ status }) => (
  <span
    role="img"
    aria-label={LABEL[status] ?? status}
    title={LABEL[status] ?? status}
    className={cn("flex size-5 shrink-0 items-center justify-center", TONE[status])}
  >
    <span className="size-2 rounded-full bg-current" />
  </span>
);

export const StatusMenu: FC<{
  status: SectionStatus;
  onChange: (next: SectionStatus) => void;
  disabled?: boolean;
}> = ({ status, onChange, disabled }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild disabled={disabled}>
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        aria-label={`Status: ${LABEL[status]}. Change status`}
        className={cn(
          "-ml-1 inline-flex items-center gap-1.5 rounded-[5px] px-1 py-px text-xs font-semibold transition-colors duration-fast",
          "hover:bg-hover data-[state=open]:bg-hover disabled:pointer-events-none",
          TONE[status]
        )}
      >
        <span className="size-1.5 rounded-full bg-current" />
        {LABEL[status]}
        <ChevronDownIcon className="size-2.5 opacity-70" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" sideOffset={4} className="w-48" onClick={(e) => e.stopPropagation()}>
      {SECTION_STATUSES.map((s) => (
        <DropdownMenuItem key={s} onSelect={() => s !== status && onChange(s)} className="gap-2">
          <span className={cn("size-[7px] rounded-full bg-current", TONE[s])} />
          <span className="flex-1">{LABEL[s]}</span>
          {s === status && <CheckIcon className="size-3.5 text-fg-3" />}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

/** The web app's next step for a status, if there is one. */
export const nextStep = (
  status: SectionStatus
): { label: string; kind: "open" } | { label: string; kind: "set"; to: SectionStatus } | null => {
  if (status === "In Progress") return { label: "Send for review", kind: "open" };
  if (status === "In review") return { label: "Mark review as complete", kind: "set", to: "Finalising" };
  if (status === "Finalising") return { label: "Mark as complete", kind: "set", to: "Complete" };
  return null;
};
